import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { UserActivityService } from '@/services/analytics/userActivityService';
// Create a simple hook to get user info without importing the context
const useAuthContext = () => {
  return { user: null }; // Simplified for now - will work without authentication
};

// Generate or get session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('user_activity_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('user_activity_session_id', sessionId);
  }
  return sessionId;
};

// Get device info
const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    windowSize: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer,
    touchSupport: 'ontouchstart' in window,
    cookieEnabled: navigator.cookieEnabled,
    onlineStatus: navigator.onLine
  };
};

export const useUserActivityTracking = () => {
  const location = useLocation();
  const { user } = useAuthContext();
  const sessionId = useRef(getSessionId());
  const pageStartTime = useRef(Date.now());
  const activityQueue = useRef<Array<() => Promise<void>>>([]);
  const isProcessingQueue = useRef(false);

  // Process activity queue
  const processQueue = useCallback(async () => {
    if (isProcessingQueue.current || activityQueue.current.length === 0) return;
    
    isProcessingQueue.current = true;
    const activities = [...activityQueue.current];
    activityQueue.current = [];

    try {
      await Promise.all(activities.map(activity => activity()));
    } catch (error) {
      console.error('Error processing activity queue:', error);
    } finally {
      isProcessingQueue.current = false;
    }
  }, []);

  // Queue activity for batch processing
  const queueActivity = useCallback((activity: () => Promise<void>) => {
    activityQueue.current.push(activity);
    // Process queue after a small delay to batch activities
    setTimeout(processQueue, 100);
  }, [processQueue]);

  // Track page view and session
  useEffect(() => {
    const startTime = Date.now();
    pageStartTime.current = startTime;

    // Start/update session
    queueActivity(() => 
      UserActivityService.startSession(sessionId.current, {
        userId: user?.id,
        deviceInfo: getDeviceInfo()
      })
    );

    // Track page view
    queueActivity(() =>
      UserActivityService.trackActivity(
        sessionId.current,
        'page_view',
        location.pathname,
        {
          userId: user?.id,
          deviceInfo: getDeviceInfo(),
          metadata: {
            referrer: document.referrer,
            timestamp: new Date().toISOString()
          }
        }
      )
    );

    // Track page leave and duration when location changes or component unmounts
    return () => {
      const duration = Math.round((Date.now() - startTime) / 1000);
      queueActivity(() =>
        UserActivityService.trackActivity(
          sessionId.current,
          'page_leave',
          location.pathname,
          {
            userId: user?.id,
            durationSeconds: duration,
            metadata: {
              pageLoadTime: startTime,
              totalTimeOnPage: duration
            }
          }
        )
      );
    };
  }, [location.pathname, user?.id, queueActivity]);

  // Track session end on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      UserActivityService.endSession(sessionId.current);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Track scroll events
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let lastScrollY = window.scrollY;
    let maxScroll = 0;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollPercent = Math.round((currentScrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        queueActivity(() =>
          UserActivityService.trackActivity(
            sessionId.current,
            'scroll',
            location.pathname,
            {
              userId: user?.id,
              metadata: {
                scrollPosition: currentScrollY,
                scrollPercent: scrollPercent,
                maxScrollReached: maxScroll,
                scrollDirection: currentScrollY > lastScrollY ? 'down' : 'up'
              }
            }
          )
        );
        lastScrollY = currentScrollY;
      }, 1000);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [location.pathname, user?.id, queueActivity]);

  // Track click events
  const trackClick = useCallback((elementId: string, elementType: string, metadata?: Record<string, any>) => {
    queueActivity(() =>
      UserActivityService.trackActivity(
        sessionId.current,
        'click',
        location.pathname,
        {
          userId: user?.id,
          elementId,
          elementType,
          metadata
        }
      )
    );
  }, [location.pathname, user?.id, queueActivity]);

  // Track form interactions
  const trackFormInteraction = useCallback((formId: string, action: 'start' | 'submit' | 'abandon', metadata?: Record<string, any>) => {
    queueActivity(() =>
      UserActivityService.trackActivity(
        sessionId.current,
        `form_${action}`,
        location.pathname,
        {
          userId: user?.id,
          elementId: formId,
          elementType: 'form',
          metadata
        }
      )
    );
  }, [location.pathname, user?.id, queueActivity]);

  // Track search interactions
  const trackSearch = useCallback((query: string, resultsCount?: number, metadata?: Record<string, any>) => {
    queueActivity(() =>
      UserActivityService.trackActivity(
        sessionId.current,
        'search',
        location.pathname,
        {
          userId: user?.id,
          elementType: 'search',
          metadata: {
            query,
            resultsCount,
            ...metadata
          }
        }
      )
    );
  }, [location.pathname, user?.id, queueActivity]);

  // Track content interactions
  const trackContentInteraction = useCallback((
    contentId: string, 
    contentType: string, 
    action: string, 
    metadata?: Record<string, any>
  ) => {
    queueActivity(() =>
      UserActivityService.trackActivity(
        sessionId.current,
        `content_${action}`,
        location.pathname,
        {
          userId: user?.id,
          elementId: contentId,
          elementType: contentType,
          metadata
        }
      )
    );
  }, [location.pathname, user?.id, queueActivity]);

  // Track feature usage
  const trackFeatureUsage = useCallback((featureName: string, metadata?: Record<string, any>) => {
    queueActivity(() =>
      UserActivityService.trackActivity(
        sessionId.current,
        'feature_usage',
        location.pathname,
        {
          userId: user?.id,
          elementId: featureName,
          elementType: 'feature',
          metadata
        }
      )
    );
  }, [location.pathname, user?.id, queueActivity]);

  // Track errors
  const trackError = useCallback((errorType: string, errorMessage: string, metadata?: Record<string, any>) => {
    queueActivity(() =>
      UserActivityService.trackActivity(
        sessionId.current,
        'error',
        location.pathname,
        {
          userId: user?.id,
          elementType: 'error',
          metadata: {
            errorType,
            errorMessage,
            ...metadata
          }
        }
      )
    );
  }, [location.pathname, user?.id, queueActivity]);

  return {
    sessionId: sessionId.current,
    trackClick,
    trackFormInteraction,
    trackSearch,
    trackContentInteraction,
    trackFeatureUsage,
    trackError
  };
};